import { PublicApi } from '../types/player-api-types.ts';

export const MUX_PERCENTAGE_TO_ENABLE = 0.1;

/**
 * Whether the current environment provides the `console` methods that mux-embed
 * relies on at module-evaluation time.
 *
 * mux-embed sets up its logger via `console.trace`/`console.info`/`console.debug`
 * as soon as the module is imported (`var q = kt("[mux]")`). In restricted
 * WebKit environments such as Apple Mail's email preview, those methods are
 * undefined, so simply importing wistia-mux.js throws an error
 * @returns {boolean} True if the console methods mux-embed needs are available.
 */
const isMuxEnvironmentSupported = (): boolean =>
  typeof console !== 'undefined' &&
  // eslint-disable-next-line no-console
  typeof console.trace === 'function' &&
  // eslint-disable-next-line no-console
  typeof console.info === 'function' &&
  // eslint-disable-next-line no-console
  typeof console.debug === 'function';

/**
 * Determines if Mux should be enabled for an embed based on various conditions.
 * @param {PublicApi} video The embed mux should be enabled for.
 * @param {boolean} didWinCoinFlip Whether or not the embed won the coin flip which determines if Mux should be enabled.
 * @returns {boolean} True if Mux should be enabled, false otherwise.
 */
export const shouldEnableMux = (video: PublicApi, didWinCoinFlip: boolean): boolean => {
  const isMuxEnabledInEnvironment = isMuxEnvironmentSupported();
  const isMuxEnabledOnWindow = window.wistiaDisableMux !== true;
  const isMuxEnabledFromOpts = video._opts?.mux !== false;
  const isMuxEnabledFromStandardEmbed = video.iframe == null;
  const isMuxEnabledFromCoinFlip = didWinCoinFlip;
  const isMuxEnabledFromLiveStream = video._mediaData?.type === 'LiveStream';

  // Mux should be enabled if all of the following are true:
  // 1. The environment provides the console methods mux-embed needs
  // 3. Mux is not disabled from the embed options
  // 4. Mux is not disabled from the video being an iframe embed
  // 5. Mux is not disabled from the coin flip OR the video is a live stream
  // (Live streams should always have Mux enabled, regardless of the coin flip result)
  return (
    isMuxEnabledInEnvironment &&
    isMuxEnabledOnWindow &&
    isMuxEnabledFromOpts &&
    isMuxEnabledFromStandardEmbed &&
    (isMuxEnabledFromCoinFlip || isMuxEnabledFromLiveStream)
  );
};
